import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";

type ProductKey =
  | "Personal Auto"
  | "Commercial Auto or Trucking"
  | "Home"
  | "Condo"
  | "Renters"
  | "Rental Property"
  | "Term Life"
  | "Individual & Family Health"
  | "Employee Group Health"
  | "Business Owners Package"
  | "Work Comp"
  | "Motorcycle"
  | "Boat & Jet Ski"
  | "Umbrella";

const PRODUCT_OPTIONS: ProductKey[] = [
  "Personal Auto",
  "Commercial Auto or Trucking",
  "Home",
  "Condo",
  "Renters",
  "Rental Property",
  "Term Life",
  "Individual & Family Health",
  "Employee Group Health",
  "Business Owners Package",
  "Work Comp",
  "Motorcycle",
  "Boat & Jet Ski",
  "Umbrella",
];

const HOW_FOUND_OPTIONS = [
  "Google Search",
  "Facebook Page/Post",
  "Facebook/Instagram Ad",
  "Google Ad",
  "Customer Referral",
  "Other",
] as const;

interface IQuoteForm_DATA {
  products: ProductKey[];
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  zipCode: string;

  howFound: (typeof HOW_FOUND_OPTIONS)[number] | "";
  currentProvider: string;
  dateNeeded: string; // yyyy-mm-dd
  comments: string;

  // spam controls
  captchaAnswer: string;
  honeypot: string; // should remain empty

  domainName:string;
  itemCategory:string;
  itemClass:string;
  itemType:string;
  clientVersion: string;

}

type Errors = Partial<Record<keyof IQuoteForm_DATA, string>>;

const default_QuoteForm_DATA: IQuoteForm_DATA = {
  products: [],
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  zipCode: "",

  howFound: "",
  currentProvider: "",
  dateNeeded: "",
  comments: "",
  captchaAnswer: "",
  honeypot: "",

  domainName: window.location.hostname,
  itemCategory: 'Customer',
  itemClass: 'quote',
  itemType: 'legacy.requestQuote',
  clientVersion: "v01a",
};

const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";


export default function InsuranceQuoteForm({
  onSubmit,
}: {
  onSubmit?: (aForm_DATA: IQuoteForm_DATA) => Promise<void> | void;
}) {
  const [actualForm_DATA, setData] = useState<IQuoteForm_DATA>(default_QuoteForm_DATA);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<null | "ok" | "fail">(null);

  // simple math captcha
  const captcha = useMemo(() => {
    const a = Math.floor(Math.random() * 5) + 3; // 3..7
    const b = Math.floor(Math.random() * 5) + 2; // 2..6
    return { a, b, sum: a + b };
  }, []); // stable per mount

  function set<K extends keyof IQuoteForm_DATA>(key: K, value: IQuoteForm_DATA[K]) {
    setData((d) => ({ ...d, [key]: value }));
  }

  function toggleProduct(p: ProductKey) {
    setData((d) => {
      const exists = d.products.includes(p);
      return {
        ...d,
        products: exists ? d.products.filter((x) => x !== p) : [...d.products, p],
      };
    });
  }

  function validate(d: IQuoteForm_DATA): Errors {
    const e: Errors = {};
    if (d.products.length === 0) e.products = "Select at least one coverage type.";
    if (!d.firstName.trim()) e.firstName = "First name is required.";
    if (!d.lastName.trim()) e.lastName = "Last name is required.";
    if (!d.phone.trim()) e.phone = "Phone number is required.";
    if (!/^\+?[\d().\s-]{7,}$/.test(d.phone.trim()))
      e.phone = "Enter a valid phone number.";
    if (!d.email.trim()) e.email = "Email is required.";
    if (
      d.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())
    )
      e.email = "Enter a valid email address.";
    if (!d.howFound) e.howFound = "Please tell us how you found us.";
    if (!d.dateNeeded) e.dateNeeded = "Please select a date.";
    // captcha
    if (String(captcha.sum) !== d.captchaAnswer.trim())
      e.captchaAnswer = "Captcha answer is incorrect.";
    // honeypot
    if (d.honeypot) e.honeypot = "Spam detected.";
    return e;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setSubmitted(null);
    const e = validate(actualForm_DATA);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmitting(true);
    try {
      // Plug your API here. Example payload:
      const payload = {
        ...actualForm_DATA,
        // nice formatting for the products
        products: actualForm_DATA.products,
        submittedAt: new Date().toISOString(),
      };

      if (onSubmit) {
        await onSubmit(actualForm_DATA);
      } else {
        // demo: simulate network
        await new Promise((r) => setTimeout(r, 900));
        // If you want to POST:
         await fetch(API_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
      }

      setSubmitted("ok");
      setData(default_QuoteForm_DATA);
      // keep captcha the same until unmount
    } catch (err) {
      console.error("submit.error:", err);
      setSubmitted("fail");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container className="my-4">
      <Row className="g-4">
        <Col lg={8}>
          <Card>
            <Card.Body>
              <Card.Title as="h2" className="h4 mb-3">
                START YOUR QUOTE BELOW
              </Card.Title>
              <Card.Subtitle className="mb-4 text-muted">
                Enter some basic info below to start the quote process
              </Card.Subtitle>

              {submitted === "ok" && (
                <Alert variant="success" className="mb-4">
                  Thanks! Your request was sent. We’ll reach out soon.
                </Alert>
              )}
              {submitted === "fail" && (
                <Alert variant="danger" className="mb-4">
                  Sorry—something went wrong. Please try again.
                </Alert>
              )}

              <Form noValidate onSubmit={handleSubmit}>
                {/* What would you like a quote for? */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    What would you like a quote for? <span className="text-danger">*</span>
                  </Form.Label>
                
                
                  <Row xs={1} sm={2} md={2} lg={2}>
                    {PRODUCT_OPTIONS.map((p) => (
                      <Col key={p} className="mb-2">
                        <Form.Check
                          id={`product-${p}`}
                          type="checkbox"
                          label={p}
                          checked={actualForm_DATA.products.includes(p)}
                          onChange={() => toggleProduct(p)}
                        />
                      </Col>
                    ))}
                  </Row>


                  {errors.products && (
                    <div className="text-danger small mt-1">{errors.products}</div>
                  )}
                </Form.Group>

                {/* Name */}
                <Row className="mb-3">
                  <Form.Group as={Col} md={6} controlId="firstName">
                    <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      value={actualForm_DATA.firstName}
                      onChange={(e) => set("firstName", e.target.value)}
                      isInvalid={!!errors.firstName}
                      placeholder="First"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.firstName}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={6} controlId="lastName">
                    <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      value={actualForm_DATA.lastName}
                      onChange={(e) => set("lastName", e.target.value)}
                      isInvalid={!!errors.lastName}
                      placeholder="Last"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.lastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>

                {/* Phone / Email */}
                <Row className="mb-3">
                  <Form.Group as={Col} md={6} controlId="phone">
                    <Form.Label>Your Phone Number <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      value={actualForm_DATA.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      isInvalid={!!errors.phone}
                      placeholder="(555) 555-5555"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={6} controlId="email">
                    <Form.Label>Your Email <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      value={actualForm_DATA.email}
                      onChange={(e) => set("email", e.target.value)}
                      isInvalid={!!errors.email}
                      placeholder="you@example.com"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>

                {/* How found / Provider */}
                <Row className="mb-3">
                  <Form.Group as={Col} md={6} controlId="howFound">
                    <Form.Label>How did you find our agency? <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={actualForm_DATA.howFound}
                      onChange={(e) => set("howFound", e.target.value as any)}
                      isInvalid={!!errors.howFound}
                    >
                      <option value="">Select one...</option>
                      {HOW_FOUND_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.howFound}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={6} controlId="currentProvider">
                    <Form.Label>Current Insurance Provider</Form.Label>
                    <Form.Control
                      value={actualForm_DATA.currentProvider}
                      onChange={(e) => set("currentProvider", e.target.value)}
                      placeholder="Current Insurance Company"
                    />
                  </Form.Group>
                </Row>

                {/* Date needed */}
                <Form.Group className="mb-3" controlId="dateNeeded">
                  <Form.Label>Date Quote Needed <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={actualForm_DATA.dateNeeded}
                    onChange={(e) => set("dateNeeded", e.target.value)}
                    isInvalid={!!errors.dateNeeded}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.dateNeeded}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Comments */}
                <Form.Group className="mb-3" controlId="comments">
                  <Form.Label>
                    If you have any other questions, comments or requests, please leave them here, thank you!
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={actualForm_DATA.comments}
                    onChange={(e) => set("comments", e.target.value)}
                    placeholder="Your message"
                  />
                </Form.Group>

                {/* Honeypot (hidden) */}
                <Form.Group className="mb-3" controlId="website" style={{ display: "none" }}>
                  <Form.Label>Company Website</Form.Label>
                  <Form.Control
                    value={actualForm_DATA.honeypot}
                    onChange={(e) => set("honeypot", e.target.value)}
                    placeholder="Leave this blank"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </Form.Group>

                {/* CAPTCHA */}
                <Form.Group className="mb-4" controlId="captcha">
                  <Form.Label>CAPTCHA: What is {captcha.a} + {captcha.b}? <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={actualForm_DATA.captchaAnswer}
                    onChange={(e) => set("captchaAnswer", e.target.value)}
                    isInvalid={!!errors.captchaAnswer}
                    inputMode="numeric"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.captchaAnswer}
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Submitting…
                      </>
                    ) : (
                      "Submit Quote Request"
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => {
                      setData(default_QuoteForm_DATA);
                      setErrors({});
                      setSubmitted(null);
                    }}
                    disabled={submitting}
                  >
                    Reset
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>




        <Col lg={4}>

          <Card>
            
            <Card.Body>
            <Card.Title as="h3" className="h5">Contact Our Office</Card.Title>


            <strong>Legacy Insurance Network LLC</strong>
              
              <br></br>
                1312 17th St, Suite: 364
                <br />
                Denver, CO 80202
              <br></br>
             
                <Container className="p-0 mt-1">
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "max-content max-content", // one column for labels, one for numbers
                      columnGap: "6px",  // tight gap between label and number
                      rowGap: "1px",
                      alignItems: "baseline",
                    }}
                  >
                    <span className="fw-boldx">Phone:</span>
                    <span><a href={"tel:303-337-4433"}>{"(303) 337-4433"}</a></span>

                    <span className="fw-boldx">Fax:</span>
                    <span><a href={"tel:" + "303-337-5434"}>{"(303) 337-5434"}</a></span>
                  </div>
                   <div className="mt-1">
                    <a href="mailto:info@LegacyInsuranceNetwork.com">info@LegacyInsuranceNetwork.com</a>
                  </div>
                </Container>
              </Card.Body>
          </Card>


          <Card>
            <Card.Body>




            
                
                
                
                <strong>Atrium Insurance Group</strong><br></br>
                6970 S. Holly Cir. Suite: 104, 
                <br />
                Centinnial, CO, 80122
                <br></br>




             <Container className="p-0 mt-1">
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "max-content max-content", // one column for labels, one for numbers
                      columnGap: "6px",  // tight gap between label and number
                      rowGap: "4px",
                      alignItems: "baseline",
                    }}
                  >
                    <span className="fw-boldx">Phone:</span>
                    <span><a href={"tel:303-337-4433"}>{"(303) 337-4433"}</a></span>

                    <span className="fw-boldx">Fax:</span>
                    <span><a href={"tel:" + "303-337-5434"}>{"(303) 337-5434"}</a></span>
                  </div>


                  <div className="mt-1">
                    <a href="mailto:info@LegacyInsuranceNetwork.com">info@LegacyInsuranceNetwork.com</a>
                  </div>
                </Container>
            </Card.Body>
          </Card> 










           {/* <Card>
            <Card.Body>
              <Card.Title as="h3" className="h5">Contact Our Office</Card.Title>
              <div className="mb-2 fw-semibold">StreetSmart Insurance</div>
              <div>208 South St</div>
              <div>Freehold, NJ 07728</div>
              <div className="mt-2">
                <a href="https://maps.google.com/?q=208%20South%20St%20Freehold%20NJ%2007728" target="_blank" rel="noreferrer">
                  Get Directions
                </a>
              </div>
              <div className="mt-2">
                <a href="tel:+17324628343">(732) 462-8343</a>
              </div>
              <div className="mt-1">
                <a href="mailto:hello@streetsmart.insurance">hello@streetsmart.insurance</a>
              </div>
              <hr />
              <div><a href="#">Other Links</a></div>
            </Card.Body>
          </Card>  */}
          </Col>
      </Row>
    </Container>
  );
}
